/**
 * CompletionStrip — the "Set up this customer · 3 of 7 set" card
 * sitting between the cover hero and the stat strip on the detail
 * page. Replaces the upfront 13-field create form with a visible
 * incremental-completion surface.
 *
 * Each row is one of three shapes:
 *   · `done`    — auto-filled by GSTN or already on the record.
 *                 Right-side action quietly attributes the source.
 *   · `todo`    — user-actionable improvement. Right-side action
 *                 opens the inline editor for that field.
 *   · `deferred`— intentionally left blank for now. Flint will ask
 *                 at the moment it matters (first invoice, founder
 *                 approval, etc). Right-side action says "Ask later"
 *                 to make the deferral explicit.
 *
 * Rows are computed from the customer row plus a list of TODO
 * action callbacks supplied by the parent — that way the parent
 * owns *which* editor opens, while this component owns *whether*
 * to show the row at all and *how*.
 *
 * The strip hides itself when nothing is `todo` or `deferred` —
 * i.e. once the operator has either filled or skipped everything,
 * the strip retires and the detail page returns to its normal
 * mature-record shape.
 */
import * as React from "react";
import { Check, ChevronRight } from "lucide-react";
import type { customers as customersContract } from "@flint/api-contract";
import { cn } from "@flint/ui";
import { InheritedPill } from "./inherited-pill.js";

type CustomerRow = customersContract.CustomerRow;

export interface CompletionStripActions {
  /** Open the inline editor for primary contact + WhatsApp + email. */
  onEditContact: () => void;
  /** Open the Tax-defaults dialog (used by the TDS-on-collections row). */
  onEditTax: () => void;
  /** Open the Terms & credit dialog (used by the Credit-limit row). */
  onEditTerms: () => void;
  /** Dismiss the strip entirely. Caller persists the "skipped" choice. */
  onSkipAll: () => void;
}

export interface CompletionStripProps {
  customer: CustomerRow;
  actions: CompletionStripActions;
  /** Default credit limit shown in the deferred row. Stringified rupees. */
  companyDefaultCreditLimit?: string;
  className?: string;
}

interface DoneRow {
  kind: "done";
  label: string;
  value: string;
  source: string;
}

interface TodoRow {
  kind: "todo";
  label: string;
  hint: string;
  onAction: () => void;
}

interface DeferredRow {
  kind: "deferred";
  label: string;
  hint: string;
  inherited: { label: string };
}

type Row = DoneRow | TodoRow | DeferredRow;

export function CompletionStrip({
  customer,
  actions,
  companyDefaultCreditLimit = "Rs 5L default",
  className,
}: CompletionStripProps) {
  const rows = useRows(customer, actions, companyDefaultCreditLimit);
  const doneCount = rows.filter((r) => r.kind === "done").length;
  const openCount = rows.length - doneCount;

  if (openCount === 0) return null;

  return (
    <section
      aria-label="Finish setting up this customer"
      className={cn(
        "rounded-md border border-line bg-paper shadow-card overflow-hidden",
        className,
      )}
    >
      <div
        aria-hidden
        className="h-[3px]"
        style={{
          background: `linear-gradient(90deg, var(--sky-600) 0%, var(--sky-600) ${(doneCount / rows.length) * 100}%, var(--line) ${(doneCount / rows.length) * 100}%, var(--line) 100%)`,
        }}
      />
      <header className="flex items-start justify-between gap-3 px-[18px] pt-3.5 pb-2">
        <div className="min-w-0">
          <h2 className="m-0 text-sm font-bold text-ink inline-flex items-center gap-2">
            Set up this customer
            <span className="font-mono text-xs font-semibold text-muted bg-raised px-2 py-0.5 rounded-full -tracking-[0.01em]">
              {doneCount} of {rows.length} set
            </span>
          </h2>
          <p className="mt-0.5 text-xs text-muted leading-snug">
            Fill the rest as you have time — Flint will ask for anything truly required at the
            moment you raise the first invoice.
          </p>
        </div>
        <button
          type="button"
          onClick={actions.onSkipAll}
          className={cn(
            "self-center flex-shrink-0 px-2 py-1 rounded-md text-xs text-muted",
            "hover:bg-raised hover:text-ink-2",
          )}
        >
          Skip all · I&apos;ll add as I go
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-line">
        {rows.map((row, idx) => (
          <Row key={`${row.kind}-${row.label}`} row={row} isLastPair={idx >= rows.length - 2} />
        ))}
      </div>
    </section>
  );
}

function Row({ row, isLastPair }: { row: Row; isLastPair: boolean }) {
  const isDone = row.kind === "done";
  return (
    <div
      className={cn(
        "flex items-start gap-3 px-[18px] py-3 bg-paper",
        "border-b border-line sm:border-r sm:[&:nth-child(2n)]:border-r-0",
        isLastPair && "border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0",
        "hover:bg-raised transition-colors duration-100",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "grid place-items-center w-5 h-5 rounded-full flex-shrink-0 mt-px",
          isDone ? "bg-ok text-paper" : "bg-paper border-[1.5px] border-dashed border-line-2",
        )}
      >
        {isDone ? <Check strokeWidth={3} className="w-3 h-3" /> : null}
      </span>

      <div className="flex-1 min-w-0 flex flex-col">
        <span className="text-xs font-semibold text-ink leading-snug">
          {row.kind === "deferred" ? (
            <>
              {row.label} <InheritedPill label={row.inherited.label} className="ml-1.5" />
            </>
          ) : (
            row.label
          )}
        </span>
        <span className="text-xs text-muted leading-snug">
          {row.kind === "done" ? (
            <span className="before:content-['✓_'] before:text-ok before:font-bold">
              {row.value}
            </span>
          ) : row.kind === "todo" ? (
            row.hint
          ) : (
            row.hint
          )}
        </span>
      </div>

      {row.kind === "done" ? (
        <span className="self-center text-xs font-medium text-muted px-2 py-1 flex-shrink-0">
          {row.source}
        </span>
      ) : row.kind === "todo" ? (
        <button
          type="button"
          onClick={row.onAction}
          className={cn(
            "self-center text-xs font-semibold text-sky-700 px-2 py-1 rounded-md",
            "inline-flex items-center gap-1 flex-shrink-0",
            "hover:bg-sky-100",
          )}
        >
          Add <ChevronRight strokeWidth={2.4} className="w-3 h-3" />
        </button>
      ) : (
        <span className="self-center text-xs font-medium text-muted px-2 py-1 flex-shrink-0">
          Ask later
        </span>
      )}
    </div>
  );
}

/**
 * Compute the rows from the customer record.
 *
 * Rules:
 *   · Legal identity / address / tax treatment land as DONE when
 *     the GSTN-derived fields are populated. They never become TODO
 *     (the user can't add a legal name — they'd add a GSTIN, which
 *     is a different action).
 *   · Contact + phone collapse to a single TODO when both are
 *     missing. Otherwise it's DONE.
 *   · Display name is TODO when null; falls back to legal name.
 *   · Credit limit and TDS-on-collections are TODO when unset — they
 *     deep-link into the Terms and Tax dialogs respectively (both
 *     persist into real columns post-migrations 0030 / 0031 / 0032).
 *     V1.0 originally treated them as DEFERRED ("ask later") because
 *     the schema didn't back them; that constraint is gone.
 */
function useRows(
  customer: CustomerRow,
  actions: CompletionStripActions,
  companyDefaultCreditLimit: string,
): Row[] {
  return React.useMemo(() => {
    const rows: Row[] = [];

    if (customer.gstin && customer.name) {
      rows.push({
        kind: "done",
        label: "Legal identity",
        value: `${customer.name} · GSTIN ${customer.gstin}`,
        source: "From GSTN",
      });
    }
    if (customer.addressLine1 && customer.city && customer.state) {
      rows.push({
        kind: "done",
        label: "Registered address",
        value: `${customer.addressLine1} · ${customer.city} · ${customer.state}`,
        source: "From GSTN",
      });
    }
    if (customer.gstin) {
      // Real tax-treatment summary from the customer's stored defaults.
      // V1.0 was hardcoded "CGST + SGST 18% · regular registration ·
      // From GSTN" — wrong on three counts: the mode varies per-invoice
      // by place-of-supply, the rate depends on HSN, and these aren't
      // pulled from GSTN (operator-edited).
      const modeLabel =
        customer.taxMode === "igst"
          ? "IGST"
          : customer.taxMode === "exempt"
            ? "Exempt"
            : "CGST + SGST";
      const rateLabel = customer.defaultGstRatePct
        ? `${customer.defaultGstRatePct}% default`
        : "rate per HSN";
      rows.push({
        kind: "done",
        label: "Default tax treatment",
        value: `${modeLabel} · ${rateLabel}`,
        source: "Customer record",
      });
    }

    if (!customer.contactName || !customer.phone) {
      rows.push({
        kind: "todo",
        label: "Primary contact + WhatsApp",
        hint: "For invoice + reminder delivery. Skip → falls back to email at the GST-registered address.",
        onAction: actions.onEditContact,
      });
    } else {
      rows.push({
        kind: "done",
        label: "Primary contact",
        value: `${customer.contactName} · ${customer.phone}`,
        source: "Manual",
      });
    }

    if (!customer.creditLimitPaise) {
      rows.push({
        kind: "todo",
        label: "Credit limit",
        hint: `Falls back to the company default (${companyDefaultCreditLimit}) when blank. Set a per-buyer cap if this customer should be tighter.`,
        onAction: actions.onEditTerms,
      });
    } else {
      rows.push({
        kind: "done",
        label: "Credit limit",
        value: paiseStringToRupeeLabel(customer.creditLimitPaise),
        source: "Manual",
      });
    }

    if (!customer.isTdsApplicable) {
      rows.push({
        kind: "todo",
        label: "TDS on collections",
        hint: "If this buyer deducts TDS from your invoices, set the section so receipts pre-fill the deduction.",
        onAction: actions.onEditTax,
      });
    } else {
      rows.push({
        kind: "done",
        label: "TDS on collections",
        value: customer.tdsSection
          ? `Section ${customer.tdsSection}`
          : "Applicable · section pending",
        source: "Manual",
      });
    }

    return rows;
  }, [customer, actions, companyDefaultCreditLimit]);
}

function paiseStringToRupeeLabel(paise: string): string {
  try {
    const rupees = BigInt(paise) / 100n;
    return `₹${new Intl.NumberFormat("en-IN").format(Number(rupees))}`;
  } catch {
    return "—";
  }
}
